// Volle Task-Liste fuer die /tasks-Page. Aggregiert /v2-api/tasks
// (TaskItem[]) und /v2-api/tasks/failing (offene Failures). Der
// Aggregat-Store `tasks-summary` bleibt fuer die Dashboard-Card -- der
// hier ist die Detail-Quelle, weil die Page jeden Override + jede
// Run-Historie pro Task braucht und das nicht ins Aggregat passt.
//
// Laufzustand per Protokoll statt Poll (docs/protokoll.md › „Tasks über das
// Protokoll“, seit 02.11.2026): bis dahin holte der Store die Liste alle 15 s
// (3 s bei laufendem Task), 650 kB je Abruf, davon 460 kB Ausgabe und
// Ergebnis. Jetzt:
//   - REST `/v2-api/tasks?schlank=1` (ohne output/result) + `/tasks/failing`
//     beim Start und als Revalidator; die Liste allein bei task_overrides
//     (angelegt, geloescht, an/aus, Labels) -- sonst nie. Nach einer
//     Unterbrechung schickt das Protokoll je Aufgabe einen Snapshot (state-
//     Topic, keine Luecke).
//   - `task.*.lauf` (Rate normal): Snapshot + Delta je Aufgabe patchen
//     last_run, next_run und typical_duration_s im passenden Eintrag.
//     Wechselt der Lauf, werden output/result null (die Liste traegt sie
//     nicht). Ein Fehlschlag oder dessen Heilung laedt nur `/tasks/failing`
//     (klein) nach. Eine unbekannte Aufgabe (eben angelegt) laedt die Liste
//     einmal nach.
//   - `vollerLauf(runId)`: Ausgabe, Fehler und Ergebnis eines Laufs, einzeln
//     bei Bedarf ueber /v2-api/runs/{id} (kleiner Cache).
// WS-Channel `task_overrides` patcht einzelne Eintraege lokal ohne Refetch,
// damit Edits in einem anderen Browser-Tab innerhalb von ms landen.
//
// SWR: Snapshot in localStorage (Prefix `hl:sum:tasks-list`), damit
// /tasks beim Reload sofort die letzten Tasks zeigt statt "lade...".

import { SvelteMap } from "svelte/reactivity";
import { registerRevalidator } from "./revalidate.svelte";

import { getJSON } from "./api";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { abonniere } from "./protokoll/client.svelte";
import type { TaskLauf } from "./protokoll/schema.gen";
import { skaliere } from "./protokoll/skaliere";
import { isoAusMs } from "./protokoll/werte";
import { failingNeuLaden, standAelterAls, taskIdAusInstanz, taskMitLauf } from "./tasks-lauf-protokoll";
import type { RunRecord, TaskItem, TaskOverrideEvent } from "./types";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export interface FailingEvent {
  id: number;
  ts: string;
  summary: string;
  entity?: string;
  // "info" | "error" | "critical" -- critical-Failures sind NICHT
  // quittierbar (kein "gesehen"-Button), sie klaeren sich nur durch einen
  // erfolgreichen Lauf (Recovery, auto-ack). Siehe critical-events-lane.
  severity?: string;
  attrs?: { task_name?: string; server?: string; error?: string };
}

const KEY_TASKS = "tasks-list";
const KEY_FAILING = "tasks-failing";

const _tasks = new SvelteMap<string, TaskItem[]>();
const _failing = new SvelteMap<string, FailingEvent[]>();
// Der jüngste Stand je Aufgabe aus dem Protokoll. Ein Abruf der Liste legt
// ihn über die Antwort, denn die Antwort kann hinter einem schon
// eingetroffenen Stand liegen -- außer die Antwort nennt einen jüngeren Lauf
// (Protokoll unterbrochen, der Stand ist alt).
const _stand = new Map<string, TaskLauf>();
let _started = false;
let _unsub: (() => void) | undefined;
let _unsubProto: (() => void) | undefined;

// ── Liste ──────────────────────────────────────────────────────────────

function mitStand(list: TaskItem[]): TaskItem[] {
  if (_stand.size === 0) return list;
  return list.map((t) => {
    const d = _stand.get(t.id);
    return d && !standAelterAls(t, d) ? taskMitLauf(t, d, isoAusMs) : t;
  });
}

// Aus dem Protokoll kommt oft etwas (jeder Zwischenstand eines laufenden
// Laufs): gespeichert wird gebündelt.
let _persistTimer: ReturnType<typeof setTimeout> | undefined;
function persistSpaeter(): void {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = undefined;
    const list = _tasks.get("current");
    if (list) persist(KEY_TASKS, list);
  }, 2_000);
}

let _ladeListe: Promise<void> | undefined;
let _nochmal = false;

async function ladeListe(): Promise<void> {
  // Ein Abruf läuft schon: danach noch einer, falls inzwischen etwas Neues
  // anlag (statt beliebig viele gleichzeitig).
  if (_ladeListe) {
    _nochmal = true;
    return _ladeListe;
  }
  _ladeListe = (async () => {
    try {
      const res = await fetch("/v2-api/tasks?schlank=1", { credentials: "include" });
      if (res.ok) {
        const list = mitStand((await res.json()) as TaskItem[]);
        _tasks.set("current", list);
        persist(KEY_TASKS, list);
      }
    } catch {
      // ignore -- der naechste Anlass (Revalidator, Luecke) versucht's wieder
    }
  })();
  try {
    await _ladeListe;
  } finally {
    _ladeListe = undefined;
  }
  if (_nochmal) {
    _nochmal = false;
    await ladeListe();
  }
}

async function ladeFailing(): Promise<void> {
  try {
    const res = await fetch("/v2-api/tasks/failing", { credentials: "include" });
    if (res.ok) {
      const list = (await res.json()) as FailingEvent[];
      _failing.set("current", list);
      persist(KEY_FAILING, list);
    }
  } catch {
    // ignore
  }
}

async function refresh(): Promise<void> {
  await Promise.all([ladeListe(), ladeFailing()]);
}

// Fehlschlag oder Heilung: die offenen Fehlschläge neu laden. Das Ereignis
// steht schon in der Datenbank (der Runner meldet nach dem Schreiben); die
// kurze Verzögerung bündelt mehrere Aufgaben, die zugleich enden.
let _failingTimer: ReturnType<typeof setTimeout> | undefined;
function failingSpaeter(): void {
  if (_failingTimer) return;
  _failingTimer = setTimeout(() => {
    _failingTimer = undefined;
    void ladeFailing();
  }, 300);
}

// Eine Aufgabe, die die Liste nicht kennt (eben angelegt): die Liste neu
// laden, höchstens alle 5 s.
let _nachladenAm = 0;
let _nachladenTimer: ReturnType<typeof setTimeout> | undefined;
function listeNachladen(): void {
  if (_nachladenTimer) return;
  const warte = Math.max(0, _nachladenAm + 5_000 - Date.now());
  _nachladenTimer = setTimeout(() => {
    _nachladenTimer = undefined;
    _nachladenAm = Date.now();
    void ladeListe();
  }, warte);
}

function fehlerOffen(taskId: string): boolean {
  const entity = "task:" + taskId;
  return !!_failing.get("current")?.some((f) => f.entity === entity);
}

/** Ein Stand aus dem Protokoll (Drahtzahlen) → der Eintrag der Aufgabe. */
function ausProtokoll(instanz: string, daten: Record<string, unknown>): void {
  const id = taskIdAusInstanz(instanz);
  if (!id) return;
  const d = skaliere<TaskLauf>("task.lauf", daten);
  _stand.set(id, d);
  const cur = _tasks.get("current");
  // Noch keine Liste: der Abruf legt den gemerkten Stand darüber.
  if (!cur) return;
  const i = cur.findIndex((t) => t.id === id);
  if (i < 0) {
    listeNachladen();
    return;
  }
  const alt = cur[i];
  const neu = taskMitLauf(alt, d, isoAusMs);
  if (neu === alt) return;
  const next = cur.slice();
  next[i] = neu;
  _tasks.set("current", next);
  persistSpaeter();
  if (failingNeuLaden(alt.last_run, neu.last_run, fehlerOffen(id))) failingSpaeter();
}

// Lokaler Patch ohne Refetch fuer task_overrides-WS-Events. Spiegelt
// die Server-Logik fuer effective_retention_days / effective_schedule:
// Override gewinnt, sonst yaml-Default. next_run kommt aus dem Protokoll.
function applyOverride(ev: TaskOverrideEvent): void {
  const current = _tasks.get("current");
  if (!current) return;
  let touched = false;
  const next = current.map((t) => {
    if (t.id !== ev.task_id) return t;
    touched = true;
    const updated = { ...t, overrides: { ...t.overrides } };
    if (ev.type === "set" && ev.value !== undefined) {
      updated.overrides[ev.key] = ev.value;
    } else if (ev.type === "delete") {
      delete updated.overrides[ev.key];
    }
    if (ev.key === "retention_days") {
      const ov = updated.overrides["retention_days"];
      updated.effective_retention_days = typeof ov === "number"
        ? ov
        : updated.retention_days;
    } else if (ev.key === "schedule") {
      if ("schedule" in updated.overrides) {
        const ov = updated.overrides["schedule"];
        updated.effective_schedule = typeof ov === "string" && ov !== ""
          ? ov
          : null;
      } else {
        updated.effective_schedule = updated.schedule;
      }
    }
    return updated;
  });
  if (!touched) return;
  _tasks.set("current", next);
  persist(KEY_TASKS, next);
}

export function startTasksList(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(refresh);
  // SWR: gecachten Snapshot sofort einspielen.
  const cTasks = loadPersisted<TaskItem[]>(KEY_TASKS);
  if (cTasks) _tasks.set("current", cTasks);
  const cFailing = loadPersisted<FailingEvent[]>(KEY_FAILING);
  if (cFailing) _failing.set("current", cFailing);
  void refresh();
  _unsubProto = abonniere(
    "task.*.lauf",
    { rate: "normal" },
    {
      stand: ausProtokoll,
      weg: (instanz) => {
        const id = taskIdAusInstanz(instanz);
        if (id) _stand.delete(id);
      },
    },
  );
  _unsub = wsSubscribe("task_overrides", (data) => {
    const ev = data as TaskOverrideEvent | undefined;
    if (!ev) return;
    // Ein angelegter, gelöschter oder umgestellter Task (an/aus, Labels)
    // ist kein Patch an einem Override -- hier hilft nur neu laden.
    if (ev.type === "created" || ev.type === "deleted" || ev.type === "geaendert") {
      void ladeListe();
      return;
    }
    applyOverride(ev);
  });
}

export function stopTasksList(): void {
  _started = false;
  for (const t of [_persistTimer, _failingTimer, _nachladenTimer]) if (t) clearTimeout(t);
  _persistTimer = _failingTimer = _nachladenTimer = undefined;
  _nochmal = false;
  if (_unsub) {
    _unsub();
    _unsub = undefined;
  }
  if (_unsubProto) {
    _unsubProto();
    _unsubProto = undefined;
  }
  _stand.clear();
  _tasks.clear();
  _failing.clear();
  _voll.clear();
  _versucht.clear();
}

export function getTasksList(): TaskItem[] | undefined {
  return _tasks.get("current");
}

export function getTasksFailing(): FailingEvent[] | undefined {
  return _failing.get("current");
}

export function refreshTasksList(): Promise<void> {
  return refresh();
}

// Manueller Failing-Drop nach Ack -- vermeidet Refetch fuer einen
// einzelnen entfernten Eintrag.
export function dropFailing(id: number): void {
  const cur = _failing.get("current");
  if (!cur) return;
  const next = cur.filter((f) => f.id !== id);
  _failing.set("current", next);
  persist(KEY_FAILING, next);
}

// ── Volle Läufe ────────────────────────────────────────────────────────
// Ausgabe, Fehlertext und Ergebnis stehen nicht in der Liste (sie ist
// schlank, der Fehler im Protokoll auf 500 Zeichen gekürzt), sondern kommen
// je Lauf über /v2-api/runs/{id} -- einmal, wenn eine Ansicht sie zum ersten
// Mal liest. Ein beendeter Lauf ändert sich nicht mehr, darum genügt ein
// kleiner Cache (die ältesten fliegen raus).

const VOLL_MAX = 20;
const FEHLER_PAUSE_MS = 5_000;
const _voll = new SvelteMap<string, RunRecord>();
// Reaktiv: "fehler", sobald ein Abruf scheiterte (die Ansicht hört auf zu
// "laden"), "warten", solange die Datenbank den Lauf noch als laufend führt;
// beides fällt nach FEHLER_PAUSE_MS weg (spaeterNochmal). Wird nur aus dem
// Abruf selbst geschrieben, nie beim Lesen (Schreiben beim Lesen verbietet
// Svelte in $derived).
const _vollZustand = new SvelteMap<string, "fehler" | "warten">();
// Nicht reaktiv: gestartete Abrufe und solche in der Pause.
const _versucht = new Map<string, number>();

/** Der volle Lauf (Ausgabe, Fehler, Ergebnis), sobald er da ist; bis dahin
 *  `undefined` (reaktiv). Das erste Lesen stößt den Abruf an. Ein laufender Lauf
 *  (`laeuft`) wird nicht geholt -- seine Ausgabe kommt als Livestrom, und ein
 *  Abruf wäre sofort veraltet. */
export function vollerLauf(runId: string | null | undefined, laeuft = false): RunRecord | undefined {
  if (!runId) return undefined;
  const hit = _voll.get(runId);
  if (hit) return hit;
  _vollZustand.get(runId); // abonnieren: das Ende der Pause meldet sich
  if (!laeuft) {
    if (!_versucht.has(runId)) {
      _versucht.set(runId, -1);
      void holeLauf(runId);
    }
  }
  return undefined;
}

/** Wird der Lauf gerade geholt (und ist noch nicht gescheitert)? */
export function vollerLaufLaedt(runId: string | null | undefined): boolean {
  if (!runId || _voll.has(runId)) return false;
  return _vollZustand.get(runId) !== "fehler";
}

async function holeLauf(runId: string): Promise<void> {
  // Erst nach dem Lesen (das ein $derived sein kann) in reaktiven Zustand schreiben.
  await Promise.resolve();
  try {
    const r = await getJSON<RunRecord>(`/v2-api/runs/${encodeURIComponent(runId)}`);
    if (!_started) return;
    // Ein Lauf, der in der Datenbank noch läuft, ist nicht fertig: nicht
    // merken, nach der Pause noch einmal.
    if (r.status === "running") {
      spaeterNochmal(runId, "warten");
      return;
    }
    _vollZustand.delete(runId);
    _voll.set(runId, r);
    while (_voll.size > VOLL_MAX) {
      const aeltester = _voll.keys().next().value as string;
      _voll.delete(aeltester);
      _versucht.delete(aeltester);
    }
  } catch {
    spaeterNochmal(runId, "fehler");
  }
}

// Nach FEHLER_PAUSE_MS darf der Abruf wieder: der Eintrag in _versucht fällt
// weg, und das Leeren von _vollZustand (reaktiv) lässt die lesende Ansicht
// vollerLauf erneut rufen. Ohne diesen Anstoß bliebe die Ansicht leer, bis
// sich zufällig etwas anderes ändert.
function spaeterNochmal(runId: string, zustand: "fehler" | "warten"): void {
  _versucht.set(runId, Date.now());
  _vollZustand.set(runId, zustand);
  setTimeout(() => {
    if (!_started) return;
    _versucht.delete(runId);
    _vollZustand.delete(runId);
  }, FEHLER_PAUSE_MS);
}
